/* ==========================================================================
   <product-reviews>
   Fetches shared Judge.me store reviews on product pages.
   Reviews are revealed in small client-side batches to keep PDPs concise.
   ========================================================================== */

const shopReviewSummaryCache = new Map();

function loadShopReviewSummary(shopDomain, apiToken) {
  const cacheKey = `${shopDomain}:${apiToken}`;
  if (shopReviewSummaryCache.has(cacheKey)) {
    return shopReviewSummaryCache.get(cacheKey);
  }

  const params = new URLSearchParams({
    shop_domain: shopDomain,
    api_token: apiToken,
    json_request: 'true',
  });
  const request = Promise.all([
    fetch(`https://judge.me/api/v1/widgets/shop_reviews_count?${params}`),
    fetch(`https://judge.me/api/v1/widgets/shop_reviews_rating?${params}`),
  ])
    .then(async ([countResponse, ratingResponse]) => {
      if (!countResponse.ok || !ratingResponse.ok) {
        throw new Error('Judge.me summary request failed');
      }
      const [countPayload, ratingPayload] = await Promise.all([
        countResponse.json(),
        ratingResponse.json(),
      ]);
      return {
        count: Math.max(0, Number(countPayload.shop_reviews_count) || 0),
        rating: Math.max(0, Math.min(5, Number(ratingPayload.shop_reviews_rating) || 0)),
      };
    })
    .catch((error) => {
      shopReviewSummaryCache.delete(cacheKey);
      throw error;
    });

  shopReviewSummaryCache.set(cacheKey, request);
  return request;
}

class ShopReviewRating extends HTMLElement {
  connectedCallback() {
    if (this.loading || !this.dataset.apiToken || !this.dataset.shopDomain) return;
    this.loading = true;

    loadShopReviewSummary(this.dataset.shopDomain, this.dataset.apiToken)
      .then(({ count, rating }) => {
        if (!this.isConnected || !count || rating < 1 || rating > 5) return;
        const link = this.querySelector('[data-store-rating-link]');
        const stars = this.querySelector('[data-store-rating-stars]');
        const countNode = this.querySelector('[data-store-review-count]');
        if (!link || !stars || !countNode) return;

        stars.style.setProperty('--review-rating-fill', `${(rating / 5) * 100}%`);
        countNode.textContent = String(count);
        link.setAttribute(
          'aria-label',
          (this.dataset.ariaTemplate || '')
            .replace('__RATING__', rating.toFixed(1))
            .replace('__COUNT__', String(count)),
        );
        this.hidden = false;
      })
      .catch(() => {
        this.hidden = true;
      });
  }
}

class ProductReviews extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.product-reviews');
    this.list = this.querySelector('[data-reviews-list]');
    this.status = this.querySelector('[data-reviews-status]');
    this.retry = this.querySelector('[data-reviews-retry]');
    this.more = this.querySelector('[data-reviews-more]');
    this.summary = this.querySelector('[data-reviews-summary]');
    this.summaryRating = this.querySelector('[data-reviews-rating]');
    this.summaryCount = this.querySelector('[data-reviews-count]');
    this.pendingReviews = [];
    this.currentPage = 0;
    this.totalPages = 1;
    this.loading = false;

    if (!this.list || !this.dataset.apiToken || !this.dataset.shopDomain) return;

    this.retry?.addEventListener('click', () => {
      const retryPage = this.failedPage || 1;
      this.loadPage(retryPage, { reset: retryPage === 1 });
    });
    this.more?.addEventListener('click', () => {
      if (this.pendingReviews.length) {
        this.renderNextBatch({ reveal: true });
        this.updateMoreVisibility();
        return;
      }
      if (!this.loading && this.currentPage < this.totalPages) {
        this.loadPage(this.currentPage + 1);
      }
    });
    this.loadPage(1, { reset: true });
  }

  async loadPage(page, { reset = false } = {}) {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.loading = true;
    this.setBusy(true);
    this.setStatus(this.dataset.loadingMessage);
    if (reset) {
      this.list.replaceChildren();
      this.pendingReviews = [];
    }

    const params = new URLSearchParams({
      shop_domain: this.dataset.shopDomain,
      api_token: this.dataset.apiToken,
      review_type: 'shop-reviews',
      per_page: this.dataset.perPage || '4',
      page: String(page),
      json_request: 'true',
    });
    try {
      // The public widget API does not document a chronological sort parameter.
      // Gather its pages before revealing reviews so a later page cannot hide
      // the newest review behind older, featured reviews on page one.
      const collected = [];
      let totalPages = 1;
      for (let current = 1; current <= totalPages; current++) {
        params.set('page', String(current));
        const response = await fetch(`https://judge.me/api/v1/widgets/all_reviews_page?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`Judge.me request failed: ${response.status}`);
        const payload = await response.json();
        if (this.controller !== controller) return;
        totalPages = Math.max(1, Number(payload.total_pages) || 1);
        if (!Number.isInteger(totalPages) || totalPages > 50) throw new Error('Review pagination exceeds the verified limit');
        collected.push(...(Array.isArray(payload.reviews) ? payload.reviews : []));
      }
      const unique = new Map();
      collected.forEach(review => unique.set(review.id || `${review.created_at}:${review.reviewer_name}:${review.body}`, review));
      const reviews = [...unique.values()].sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
      this.currentPage = totalPages;
      this.totalPages = totalPages;
      this.failedPage = 0;

      if (!reviews.length && reset) {
        if (this.section) this.section.hidden = true;
        this.setStatus('');
      } else {
        this.pendingReviews.push(...reviews);
        if (this.section) this.section.hidden = false;
        this.renderNextBatch();
        this.setStatus('');
      }

      if (reset && reviews.length) this.loadSummary();
      this.updateMoreVisibility();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.failedPage = page;
      if (this.section) this.section.hidden = false;
      this.setStatus(this.dataset.errorMessage, { retry: true });
      if (this.more) this.more.hidden = true;
    } finally {
      if (this.controller !== controller) return;
      this.loading = false;
      this.setBusy(false);
    }
  }

  async loadSummary() {
    try {
      const { count, rating } = await loadShopReviewSummary(
        this.dataset.shopDomain,
        this.dataset.apiToken,
      );
      if (!count || !this.summary || !this.summaryCount || !this.summaryRating) return;
      this.summaryCount.textContent = String(count);
      this.summaryRating.textContent = rating.toFixed(1);
      this.summary.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') this.summary?.setAttribute('hidden', '');
    }
  }

  renderNextBatch({ reveal = false } = {}) {
    const batchSize = Math.max(2, Number(this.dataset.perPage) || 4);
    const batch = this.pendingReviews.splice(0, batchSize);
    const cards = batch.map(review => this.renderReview(review));
    cards.forEach(card => this.list.appendChild(card));
    if (reveal && cards.length) {
      cards[0].tabIndex = -1;
      cards[0].focus({ preventScroll: true });
      cards[0].scrollIntoView({ block: 'center', inline: 'start', behavior: 'auto' });
    }
  }

  updateMoreVisibility() {
    if (!this.more) return;
    this.more.hidden = (
      this.pendingReviews.length === 0
      && this.currentPage >= this.totalPages
    );
  }

  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    if (this.more) this.more.disabled = busy;
  }

  setStatus(message, { retry = false } = {}) {
    if (this.status) {
      this.status.textContent = message || '';
      this.status.hidden = !message;
    }
    if (this.retry) this.retry.hidden = !retry;
  }

  renderReview(review) {
    const article = document.createElement('article');
    article.className = 'product-review';

    const score = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)));
    const rating = document.createElement('p');
    rating.className = 'product-review__rating';
    rating.textContent = `${'★'.repeat(score)}${'☆'.repeat(5 - score)}`;
    rating.setAttribute('aria-label', `${score} / 5`);
    article.appendChild(rating);

    if (review.title) {
      const title = document.createElement('h3');
      title.className = 'product-review__title';
      title.textContent = review.title;
      article.appendChild(title);
    }

    const body = document.createElement('p');
    body.className = 'product-review__body';
    body.textContent = review.body || '';
    article.appendChild(body);

    const reviewerName = review.reviewer?.name || review.reviewer_name || '';
    const meta = document.createElement('p');
    meta.className = 'product-review__meta';

    if (reviewerName) {
      const author = document.createElement('span');
      author.className = 'product-review__author';
      author.textContent = reviewerName;
      meta.appendChild(author);
    }

    const date = this.formatDate(review.created_at);
    if (date) {
      const time = document.createElement('time');
      time.dateTime = review.created_at;
      time.textContent = date;
      meta.appendChild(time);
    }

    const verifiedStatuses = new Set([
      'confirmed-buyer',
      'buyer',
      'verified-purchase',
      'semi-verified-purchase',
      'admin',
    ]);
    const verifiedValue = review.verified || review.verified_status;
    if (verifiedValue === true || verifiedStatuses.has(verifiedValue)) {
      const verified = document.createElement('span');
      verified.className = 'product-review__verified';
      verified.textContent = this.dataset.verifiedLabel;
      meta.appendChild(verified);
    }
    if (meta.childNodes.length) article.appendChild(meta);

    const pictures = Array.isArray(review.pictures_urls)
      ? review.pictures_urls
      : (Array.isArray(review.pictures) ? review.pictures : []);
    const publishedPictures = pictures.filter(
      (picture) => picture.hidden !== true && picture.hidden !== 'true',
    );
    if (publishedPictures.length) {
      const media = document.createElement('div');
      media.className = 'product-review__media';
      publishedPictures.forEach((picture) => {
        const thumbnailSource = (
          picture.small
          || picture.original
          || picture.urls?.small
          || picture.urls?.original
          || picture.url
          || (typeof picture === 'string' ? picture : '')
        );
        const fullSource = (
          picture.original
          || picture.huge
          || picture.urls?.original
          || picture.url
          || (typeof picture === 'string' ? picture : '')
          || thumbnailSource
        );
        if (!this.safeImageUrl(thumbnailSource) || !this.safeImageUrl(fullSource)) return;

        const link = document.createElement('a');
        link.className = 'product-review__photo';
        link.href = fullSource;
        link.target = '_blank';
        link.rel = 'noopener';
        link.setAttribute('aria-label', this.dataset.photoAlt.replace('[author]', reviewerName));

        const image = document.createElement('img');
        image.src = thumbnailSource;
        image.alt = this.dataset.photoAlt.replace('[author]', reviewerName);
        image.width = 160;
        image.height = 160;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.appendChild(image);
        media.appendChild(link);
      });
      if (media.childNodes.length) article.appendChild(media);
    }

    return article;
  }

  safeImageUrl(source) {
    if (!source) return false;
    try {
      const url = new URL(source, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch (_) {
      return false;
    }
  }

  formatDate(value) {
    if (!value) return '';
    const parsed = new Date(value.replace(' UTC', 'Z').replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat(this.dataset.locale || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(parsed);
  }
}

if (!customElements.get('shop-review-rating')) {
  customElements.define('shop-review-rating', ShopReviewRating);
}
if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews);
}
